import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import styles from "./AccordionFAQ.module.css";

const faqSections = [
  {
    title: "General Questions",
    faqs: [
      { question: "How big is your team?", answer: "Our photographs & films are tailored to your wedding, and so is our team. We have a great core team of six and a fantastic team of fifteen." },
      { question: "How long are your films?", answer: "The duration of the film depends on many factors including number of events, duration of events & what kind of memory you want. We have made films that are anywhere between 15 to 90 minutes." },
      { question: "Which photobook do you provide?", answer: "We provide  Canvera photobook of 60 pages consisting of 250-260 photographs." },
      { question: "How much in advance do I need to book you?", answer: "We charge 30 percent as advance payment." },
      { question: "Are prices negotiable?", answer: "We try our best to offer you the best package, but making films and photographs is a difficult and expensive activity, and we don’t compromise on our equipment or talent." },
      { question: "How soon will I get my final products?", answer: "It depends on how soon you give us the selected photographs. From the day we receive your selected photographs, it takes about a month or two to deliver the final products." }
    ]
  },
  {
    title: "Services & Pricing",
    faqs: [
      { question: "Do you offer same day edit?", answer: "Yes we do offer same day edits, but it is not something we recommend because a hurried edit may take away the life a trailer deserves." },
      { question: "How much do your photography & films cost?", answer: "We have a wide range of packages. The final budget varies according to client’s requirements. Each wedding is unique & the budget could be a little less or much more. Contact us for personalized pricing." },
      { question: "Do you guys travel?", answer: "Yes we do! But you will have to get us there and provide accommodation." },
      { question: "How many pictures will we get at the end?", answer: "There is no limit. We don’t keep track of how many pictures we click. If there’s a moment, we will capture it." },
      { question: "How many post-processed photos will I get?", answer: "You will get around 300 post-processed, print-ready photographs for a wedding assignment. Other event photos vary depending on the type of event." },
      { question: "Do I get the complete data?", answer: "Yes, of course. We do not want to hide any memories from you, so we make sure you get the complete data." }
    ]
  }
];

const AccordionFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [scrollY, setScrollY] = useState(0); // Track scroll position
  const sectionRef = useRef(null);

  // Update scrollY state on scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollY]);

  // Function to check if section is in view
  const isInView = () => {
    if (!sectionRef.current) return false;
    const rect = sectionRef.current.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.5 && rect.bottom > 0; // 80% of viewport
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div ref={sectionRef} className={styles.faqWrapper}>
      {faqSections.map((section, secIndex) => (
        <motion.div
          key={secIndex}
          className={styles.faqSection}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView() ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }} // Animation triggers on scroll
          transition={{ duration: 0.6, ease: "easeOut", delay: secIndex * 0.3 }}
        >
          <h2 className={styles.faqTitle}>{section.title}</h2>
          <div className={styles.faqList}>
            {section.faqs.map((faq, index) => (
              <motion.div
                key={index}
                className={styles.faqItem}
                initial={{ clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)", opacity: 0 }}
                animate={isInView() ? { clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)", opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: index * 0.2 }}
              >
                <button className={styles.faqQuestion} onClick={() => toggleFAQ(`${secIndex}-${index}`)}>
                  {faq.question}
                  <motion.span
                    className={styles.iconContainer}
                    initial={{ rotate: 0 }}
                    animate={{ rotate: openIndex === `${secIndex}-${index}` ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    ▼
                  </motion.span>
                </button>
                <motion.div
                  className={styles.answerContainer}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: openIndex === `${secIndex}-${index}` ? "auto" : 0, opacity: openIndex === `${secIndex}-${index}` ? 1 : 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <p className={styles.faqAnswer}>{faq.answer}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default AccordionFAQ;
